import { z } from 'zod';
import { PRIORITIES } from '../constants';
import { bpSchema, monthSchema, paiseSchema, positivePaiseSchema, signedPaiseSchema } from '../money';
import { objectIdSchema, optionalDate, optionalObjectId, optionalText, paginationQuerySchema, patchOf } from './common';

export const PROJECT_STATUSES = ['planning', 'active', 'on_hold', 'completed', 'cancelled'] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];
export const PROJECT_OPEN_STATUSES: ProjectStatus[] = ['planning', 'active', 'on_hold'];

export const MILESTONE_STATUSES = ['pending', 'in_progress', 'completed'] as const;
export type MilestoneStatus = (typeof MILESTONE_STATUSES)[number];

export const UPDATE_VISIBILITIES = ['internal', 'client'] as const;
export type UpdateVisibility = (typeof UPDATE_VISIBILITIES)[number];
/** internal: never shown to clients · pending_review → approved | rejected for client-intended updates. */
export const UPDATE_REVIEW_STATUSES = ['internal', 'pending_review', 'approved', 'rejected'] as const;
export type UpdateReviewStatus = (typeof UPDATE_REVIEW_STATUSES)[number];

/** Sales responsibilities that can earn revenue credit. Project execution roles never do by default. */
export const SALES_CREDIT_ROLES = ['lead_generation', 'conversion', 'sales_owner'] as const;
export type SalesCreditRole = (typeof SALES_CREDIT_ROLES)[number];

export const PAYMENT_METHODS = ['bank_transfer', 'neft_rtgs', 'upi', 'cash', 'cheque', 'card', 'other'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];
export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = { bank_transfer: 'Bank transfer', neft_rtgs: 'NEFT / RTGS', upi: 'UPI', cash: 'Cash', cheque: 'Cheque', card: 'Card', other: 'Other' };

export const INVOICE_STATUSES = ['issued', 'partially_paid', 'paid', 'cancelled'] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

const stack = z.array(z.string().trim().min(1).max(40)).max(30).default([]);

const projectFields = {
  title: z.string().trim().min(2).max(160),
  description: optionalText(4000),
  category: optionalText(80),
  requirements: optionalText(8000),
  startDate: optionalDate(),
  expectedDeliveryDate: optionalDate(),
  priority: z.enum(PRIORITIES).default('medium'),
  projectManagerId: optionalObjectId(),
  memberIds: z.array(objectIdSchema).max(50).default([]),
  /** Expected value before the commercial agreement. */
  estimatedValuePaise: paiseSchema.nullable().optional(),
  /** Final contract value (accepted). Defaults to the accepted quotation's final amount. */
  contractValuePaise: paiseSchema.nullable().optional(),
  advancePaise: paiseSchema.nullable().optional(),
  paymentTerms: optionalText(2000),
  internalNotes: optionalText(8000),
  technologyStack: stack,
  /** Show this project in the client portal (only once it is active or completed). */
  clientVisible: z.boolean().default(false),
};

export const projectCreateSchema = z.object({
  clientId: objectIdSchema,
  quotationId: optionalObjectId(),
  status: z.enum(PROJECT_STATUSES).default('planning'),
  ...projectFields,
});
export type ProjectCreateInput = z.input<typeof projectCreateSchema>;

export const projectUpdateSchema = patchOf(
  z.object({
    ...projectFields,
    status: z.enum(PROJECT_STATUSES),
  }).omit({ projectManagerId: true, memberIds: true }),
);
export type ProjectUpdateInput = z.input<typeof projectUpdateSchema>;

export const projectTeamSchema = z.object({
  projectManagerId: optionalObjectId(),
  memberIds: z.array(objectIdSchema).max(50),
});

export const projectListQuerySchema = paginationQuerySchema.extend({
  status: z.enum([...PROJECT_STATUSES, 'open'] as const).optional(),
  clientId: objectIdSchema.optional(),
  mine: z.enum(['1', 'true']).optional(),
});
export type ProjectListQuery = z.input<typeof projectListQuerySchema>;

// ------------------------------------------------------------------ revenue credit
export const creditInputSchema = z
  .object({
    userId: objectIdSchema,
    role: z.enum(SALES_CREDIT_ROLES),
    method: z.enum(['percentage', 'fixed']).default('percentage'),
    shareBp: bpSchema.optional(),
    amountPaise: paiseSchema.optional(),
  })
  .refine((c) => (c.method === 'percentage' ? c.shareBp !== undefined : c.amountPaise !== undefined), 'Give a percentage share or a fixed amount');
export type CreditInput = z.input<typeof creditInputSchema>;

export const attributionReplaceSchema = z.object({
  credits: z.array(creditInputSchema).min(1).max(10),
  reason: z.string().trim().min(3).max(500),
});

export const attributionAdjustmentSchema = z.object({
  userId: objectIdSchema,
  /** Positive adds credit, negative removes it. */
  amountPaise: signedPaiseSchema.refine((v) => v !== 0, 'Adjustment cannot be zero'),
  creditMonth: monthSchema.optional(),
  reason: z.string().trim().min(3).max(500),
});

// ------------------------------------------------------------------ lead → project conversion
export const leadConvertToProjectSchema = z
  .object({
    /** Link to an existing client instead of creating one from the lead. */
    existingClientId: optionalObjectId(),
    clientName: optionalText(120),
    /** Accepted quotation backing the project. Required unless a documented agreement is given. */
    quotationId: optionalObjectId(),
    /** Commercial agreement reference when there is no quotation (PO number, signed proposal, email). */
    agreementReference: optionalText(500),
    project: z.object(projectFields),
    /** Who generated the lead (defaults to the lead's creator). */
    leadGeneratedBy: optionalObjectId(),
    /** Sales credit. Defaults to 100% for the sales owner (quotation owner or lead owner). */
    credits: z.array(creditInputSchema).max(10).optional(),
    activate: z.boolean().default(true),
  })
  .refine((v) => !!v.quotationId || !!v.agreementReference, {
    message: 'Link an accepted quotation or record the commercial agreement',
    path: ['quotationId'],
  });
export type LeadConvertToProjectInput = z.input<typeof leadConvertToProjectSchema>;

export const reopenConversionSchema = z.object({ reason: z.string().trim().min(5).max(500) });

// ------------------------------------------------------------------ milestones & updates
export const milestoneCreateSchema = z.object({
  title: z.string().trim().min(2).max(160),
  description: optionalText(2000),
  dueDate: optionalDate(),
  amountPaise: paiseSchema.nullable().optional(),
  clientVisible: z.boolean().default(true),
  order: z.coerce.number().int().min(0).max(1000).optional(),
});
export const milestoneUpdateSchema = patchOf(milestoneCreateSchema.extend({ status: z.enum(MILESTONE_STATUSES) }));

export const progressUpdateCreateSchema = z.object({
  body: z.string().trim().min(3).max(8000),
  progressPercent: z.coerce.number().int().min(0).max(100).optional(),
  visibility: z.enum(UPDATE_VISIBILITIES).default('internal'),
  milestoneId: optionalObjectId(),
});
export const progressUpdateReviewSchema = z.object({
  decision: z.enum(['approve', 'reject']),
  note: optionalText(1000),
  /** Approver may adjust the wording shown to the client. */
  clientBody: optionalText(8000),
});

// ------------------------------------------------------------------ billing
export const invoiceCreateSchema = z.object({
  title: z.string().trim().min(2).max(160),
  description: optionalText(2000),
  amountPaise: positivePaiseSchema,
  taxRateBp: bpSchema.default(0),
  issueDate: optionalDate(),
  dueDate: optionalDate(),
  milestoneId: optionalObjectId(),
  notes: optionalText(2000),
});

export const paymentRecordSchema = z.object({
  kind: z.enum(['receipt', 'refund']).default('receipt'),
  amountPaise: positivePaiseSchema,
  invoiceId: optionalObjectId(),
  method: z.enum(PAYMENT_METHODS),
  reference: optionalText(120),
  receivedAt: optionalDate(),
  note: optionalText(1000),
});
export type PaymentRecordInput = z.input<typeof paymentRecordSchema>;

export const voidReasonSchema = z.object({ reason: z.string().trim().min(3).max(500) });
